import axios from 'axios'

import { queryClient } from '@/lib/queryClient'

import { ChatApi } from '../modules/chat'

/**
 * Query key for the chat's website (`GET /chats/{id}/website`). Called without an id
 * it yields the shared prefix — use that to invalidate every chat's website after a
 * publish / unpublish / delete, from surfaces that don't know the chat.
 */
export const chatWebsiteQueryKey = (chatId?: string) =>
  chatId
    ? (['chats', 'website', chatId] as const)
    : (['chats', 'website'] as const)

/**
 * Options for the chat-website read, so surfaces that only need this one query can
 * observe the same cache entry without instantiating the whole {@link useChatQueries}
 * object (and every mutation in it). Left without `enabled` — the caller decides when
 * it should run.
 */
export const chatWebsiteQueryOptions = (chatId?: string) => ({
  queryKey: chatWebsiteQueryKey(chatId),
  queryFn: () => {
    if (!chatId) throw new Error('ID is required')
    return ChatApi.website(chatId)
  },
})

/**
 * Query key for a chat's generated HTML page descriptors
 * (`GET /chats/{id}/html-pages`, EVGPT-1565).
 */
export const chatHtmlPagesQueryKey = (chatId?: string) =>
  chatId
    ? (['chats', 'htmlPages', chatId] as const)
    : (['chats', 'htmlPages'] as const)

export const chatHtmlPagesQueryOptions = (chatId: string) => ({
  queryKey: chatHtmlPagesQueryKey(chatId),
  queryFn: () => ChatApi.htmlPages(chatId),
})

/**
 * Imperative read of the descriptor list through the same cache entry the preview
 * selector's observer uses — so opening a chat straight onto one of its pages warms
 * the selector instead of paying for a second request. Rejects when the request does.
 */
export const fetchChatHtmlPages = (chatId: string) =>
  queryClient
    .fetchQuery(chatHtmlPagesQueryOptions(chatId))
    .then((response) => response.pages)

/**
 * Query key for the chat-existence probe. Deliberately not `['chats', 'get', id]`:
 * this caches a boolean from a messages-less request, and must never be mistaken for
 * (or evict) the full chat payload that key holds.
 */
export const chatExistsQueryKey = (chatId: string) =>
  ['chats', 'exists', chatId] as const

/**
 * Whether a chat is still readable by the current user, asked as cheaply as the API
 * allows (`GET /chats/{id}` with `include_messages=false`).
 *
 * A chat is soft-deleted either by its owner or by the backend's retention sweep once
 * it goes untouched for the retention window, and neither cascades to the websites
 * published from it — those stay live with a `source_chat_id` that now 404s. `false`
 * means exactly that; anything else rethrows, so a network blip is never read as
 * "deleted".
 */
export const probeChatExists = async (chatId: string): Promise<boolean> => {
  try {
    await ChatApi.get(chatId, { include_messages: false })
    return true
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404)
      return false
    throw error
  }
}

/** `staleTime: Infinity` — a chat doesn't come back from the dead within a session,
 *  so one probe per chat id serves every card pointing at it. */
export const chatExistsQueryOptions = (chatId: string) => ({
  queryKey: chatExistsQueryKey(chatId),
  queryFn: () => probeChatExists(chatId),
  staleTime: Infinity,
  retry: false,
})

/**
 * Imperative read of {@link probeChatExists} through the same cache entry the
 * reactive `useQuery` observers use — resolves from cache when the probe already
 * ran, and de-dupes with an in-flight one. Rejects when the probe rejects.
 */
export const fetchChatExists = (chatId: string) =>
  queryClient.fetchQuery(chatExistsQueryOptions(chatId))
